import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { SLOT_MINUTES } from '@/lib/dispatchBoard';
import { toIsoDate, zonedCivilDate, zonedMinuteOfDay, type CivilDate } from '@/lib/timezone';
import type { UserSummary } from '@/routes/dispatch/useDispatchBoard';
import type { BoardItem } from '@/lib/boardItems';
import { canRemoveVisit, FINISHED_STATUSES } from '@/lib/visitRules';
import { OnMyWayPanel } from '@/components/appointments/OnMyWay';
import { useJob } from '@/routes/jobs/useJobDetail';
import { useHasPermission } from '@/auth/RequirePermission';
import { Permissions } from '@/auth/permissions';

export interface MoveRequest {
  technicianId: string | null;
  /** `YYYY-MM-DD`, or null to take the job off the schedule entirely. */
  isoDate: string | null;
  /** Minutes after local midnight. Ignored when `isoDate` is null. */
  minute: number;
}

/**
 * The keyboard route to moving a job (M1-11's AC, and CC-09).
 *
 * <b>Deliberately not an arrow-key emulation of dragging.</b> The obvious
 * approach — pick up with Enter, nudge with arrows, drop with Enter — makes a
 * keyboard user perform, one keystroke at a time, a gesture designed for a
 * pointer: eighteen presses to cross a day, with no way to know where you are
 * without counting. Stating the destination is both faster and unambiguous,
 * and it is the same two facts the drag communicates. Dispatchers are
 * keyboard-driven power users (web/CONVENTIONS.md), not people to be
 * accommodated with a slower copy of the mouse path.
 *
 * Escape, the focus trap and returning focus to the card live in the shared
 * `Dialog` (extracted at M1-04, when two more modals needed the same three
 * things). This file is the move-specific form and nothing else.
 *
 * <b>The link to the job's detail page (M1-13) lives here, not on the
 * card.</b> `JobCard` IS the drag handle and this dialog's own trigger — a
 * second click target nested inside that button would be invalid HTML and
 * would fight the pointer/drag state machine M1-11/M1-12 tuned carefully.
 * This dialog already opens on a card click and is not itself a drag
 * surface, so a plain link here satisfies "reached from the dispatch board
 * card" without touching any of that.
 */
export function MoveJobDialog({
  job,
  technicians,
  date,
  timeZone,
  slotRange,
  onMove,
  onClose,
}: {
  job: BoardItem;
  technicians: UserSummary[];
  /** The day the board is showing — the default destination date. */
  date: CivilDate;
  timeZone: string;
  /** Minutes after midnight the day's grid covers, for the time options. */
  slotRange: { startMinute: number; endMinute: number };
  onMove: (request: MoveRequest) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  const [technicianId, setTechnicianId] = useState<string>(job.assignedUserId ?? '');
  const [isoDate, setIsoDate] = useState<string>(
    job.scheduledStart ? isoDateOf(job.scheduledStart, timeZone) : toIsoDate(date),
  );
  const [minute, setMinute] = useState<number>(
    job.scheduledStart
      ? roundToSlot(zonedMinuteOfDay(new Date(job.scheduledStart), timeZone))
      : roundToSlot(slotRange.startMinute),
  );
  const [unschedule, setUnschedule] = useState(false);

  // Found in review (M2-52). Taking one of SEVERAL visits off is
  // `DELETE …/visits/{id}`, which takes no technician, so the picker is
  // disabled rather than silently ignored. Taking off a job's ONLY visit goes
  // through `/schedule` (`usePlaceJob`'s `routeFor`), which does take one —
  // that is how "take it off, unassigned" still sends the job to the rail
  // rather than leaving it with a lead and nowhere on the board.
  const technicianIgnored = unschedule && job.visitCount > 1;
  // The API refuses to take the LAST visit off a job someone is driving to or
  // working, so the option is not offered — the job page's Remove is hidden
  // in the same case, by the same rule.
  const canUnschedule = canRemoveVisit(job.visitCount, job.status);
  // One day of a multi-day job: the dialog says which, as the card does.
  const isOneOfSeveral = job.visitCount > 1;

  const slots: number[] = [];
  for (let m = roundToSlot(slotRange.startMinute); m < slotRange.endMinute; m += SLOT_MINUTES) {
    slots.push(m);
  }

  return (
    <Dialog
      titleId="move-job-title"
      title={
        isOneOfSeveral
          ? t('dispatch.move.titleVisit', { number: job.visitNumber, total: job.visitCount })
          : t('dispatch.move.title')
      }
      description={`${job.customerName} — ${job.description}`}
      onClose={onClose}
      testId="move-job-dialog"
      dismissLabel={t('dispatch.move.dismiss')}
      footer={
        <>
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            {t('dispatch.move.cancel')}
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() =>
              onMove({
                technicianId: technicianIgnored ? job.assignedUserId : technicianId || null,
                isoDate: unschedule ? null : isoDate,
                minute,
              })
            }
          >
            {t('dispatch.move.confirm')}
          </Button>
        </>
      }
    >
      <p className="text-sm">
        <Link
          to={`/jobs/${job.jobId}`}
          className="underline-offset-2 hover:underline focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none"
        >
          {t('dispatch.move.viewDetails')}
        </Link>
      </p>

      <label className="block text-sm">
        <span className="mb-1 block font-medium">{t('dispatch.move.technician')}</span>
        <select
          disabled={technicianIgnored}
          className="h-9 w-full rounded-md border border-[var(--color-input)] bg-transparent px-2 text-sm disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none"
          value={technicianIgnored ? (job.assignedUserId ?? '') : technicianId}
          onChange={(event) => setTechnicianId(event.target.value)}
        >
          <option value="">{t('dispatch.move.unassigned')}</option>
          {technicians.map((technician) => (
            <option key={technician.id} value={technician.id}>
              {technician.name}
            </option>
          ))}
        </select>
      </label>

      {canUnschedule ? (
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="size-4"
            checked={unschedule}
            onChange={(event) => setUnschedule(event.target.checked)}
          />
          <span>
            {job.visitCount > 1 ? t('dispatch.move.unscheduleVisit') : t('dispatch.move.unschedule')}
          </span>
        </label>
      ) : null}

      <label className="block text-sm">
        <span className="mb-1 block font-medium">{t('dispatch.move.date')}</span>
        <input
          type="date"
          disabled={unschedule}
          className="h-9 w-full rounded-md border border-[var(--color-input)] bg-transparent px-2 text-sm disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none"
          value={isoDate}
          onChange={(event) => setIsoDate(event.target.value)}
        />
      </label>

      <label className="block text-sm">
        <span className="mb-1 block font-medium">{t('dispatch.move.time')}</span>
        <select
          disabled={unschedule}
          className="h-9 w-full rounded-md border border-[var(--color-input)] bg-transparent px-2 text-sm disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none"
          value={minute}
          onChange={(event) => setMinute(Number(event.target.value))}
        >
          {slots.map((slot) => (
            <option key={slot} value={slot}>
              {wallClock(slot)}
            </option>
          ))}
        </select>
      </label>

      {/* M2-21: "On my way" for this visit. Here rather than on the card for
          the reason the link above is — the card is the drag handle. Only for
          a real visit of live work: a job still in the rail has no visit to
          be on the way to. */}
      {job.visitId !== null && !FINISHED_STATUSES.includes(job.status) ? (
        <BoardOnMyWay jobId={job.jobId} visitId={job.visitId} />
      ) : null}
    </Dialog>
  );
}

/**
 * The visit's "on my way", read from the job's own detail — the board's jobs
 * do not carry it (they are the list's shape), and the detail is what says
 * whether it can go now, why not, and (to the office) who it goes to.
 */
function BoardOnMyWay({ jobId, visitId }: { jobId: string; visitId: string }) {
  const detail = useJob(jobId);
  // Rule 5c, as on the job page: the addresses are the office's, whatever
  // the server sent.
  const isOffice = useHasPermission()(Permissions.manageCustomers);
  const status = detail.data?.onMyWay.find((entry) => entry.visitId === visitId);
  return <OnMyWayPanel jobId={jobId} status={status} emails={isOffice ? detail.data?.appointmentEmails : null} />;
}

function roundToSlot(minute: number): number {
  return Math.floor(minute / SLOT_MINUTES) * SLOT_MINUTES;
}

/**
 * `HH:mm` for a slot offset. Deliberately NOT run through `formatTime`: these
 * are option values in a fixed 30-minute ladder, not an instant, and a
 * locale-formatted "9:00 AM" in a `<select>` sorts and scans worse than the
 * 24-hour ladder a dispatcher is reading down.
 */
function wallClock(minute: number): string {
  const hours = Math.floor(minute / 60) % 24;
  const minutes = minute % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/** `YYYY-MM-DD` on the business's clock — what `<input type="date">` wants. */
function isoDateOf(instant: string, timeZone: string): string {
  return toIsoDate(zonedCivilDate(new Date(instant), timeZone));
}
